﻿export function initTextEncoderDecoderPage() {
    const root = document.querySelector("[data-text-tools]");
    if (!root) return;

    const page = {
        modeButtons: Array.from(root.querySelectorAll("[data-text-tool-mode]")),
        panels: Array.from(root.querySelectorAll("[data-text-tool-panel]")),
        eyebrow: root.querySelector("[data-text-tool-eyebrow]"),
        title: root.querySelector("[data-text-tool-title]"),
        description: root.querySelector("[data-text-tool-description]")
    };

    const modeInfo = {
        base64: ["Base64 Tools", "Base64 Encode / Decode", "Convert plain text to Base64 or decode Base64 back to UTF-8 text."],
        jwt: ["JWT Tools", "JWT Decode / Verify", "Decode JWT header and payload or verify HMAC-based JWT signatures."],
        hash: ["Hash Tools", "Hash Generator / Compare", "Generate SHA and MD5 hashes and compare them with expected hash values."],
        hmac: ["HMAC Tools", "HMAC Generator / Verify", "Generate HMAC signatures using a secret key and verify expected signatures."],
        url: ["URL Tools", "URL Encode / Decode", "Encode or decode URL-safe text values."],
        html: ["HTML Tools", "HTML Entity Encode / Decode", "Convert HTML-sensitive characters to entities or decode entities back to text."],
        query: ["Query Tools", "Query String Parser / Builder", "Parse URL query strings into dictionary rows or build query strings from dictionaries."],
        form: ["Form Tools", "Form Payload Parser / Builder", "Parse application/x-www-form-urlencoded payloads or build raw form payload strings."],
        case: ["Case Tools", "Text Case Converter", "Convert text into uppercase, lowercase, title case, camelCase, PascalCase, snake_case or kebab-case."]
    };

    page.modeButtons.forEach(button => {
        button.addEventListener("click", () => {
            setMode(page, button.dataset.textToolMode, modeInfo);
        });
    });

    bindCopyButtons(root);
    bindLive(root);

    runAll(root);
}

function setMode(page, mode, modeInfo) {
    page.modeButtons.forEach(button => {
        button.classList.toggle("active", button.dataset.textToolMode === mode);
    });

    page.panels.forEach(panel => {
        const active = panel.dataset.textToolPanel === mode;
        panel.hidden = !active;
        panel.classList.toggle("active", active);
        panel.classList.toggle("is-entering", active);
    });

    const info = modeInfo[mode];

    if (info) {
        if (page.eyebrow) page.eyebrow.textContent = info[0];
        if (page.title) page.title.textContent = info[1];
        if (page.description) page.description.textContent = info[2];
    }

    window.setTimeout(() => {
        page.panels.forEach(panel => panel.classList.remove("is-entering"));
    }, 420);
}

function bindLive(root) {
    root.querySelectorAll("textarea, input, select").forEach(input => {
        input.addEventListener("input", () => runAll(root));
        input.addEventListener("change", () => runAll(root));
    });
}

function runAll(root) {
    runBase64(root);
    runJwt(root);
    runHash(root);
    runHmac(root);
    runUrl(root);
    runHtml(root);
    runQuery(root);
    runForm(root);
    runCase(root);
}

/* Base64 */

function runBase64(root) {
    const input = root.querySelector("[data-text-base64-input]");
    const output = root.querySelector("[data-text-base64-output]");
    const action = root.querySelector("[data-text-base64-action]");
    const urlSafe = root.querySelector("[data-text-base64-url-safe]");

    if (!input || !output || !action) return;

    try {
        let result;

        if (action.value === "decode") {
            result = base64Decode(input.value, urlSafe?.checked);
        } else {
            result = base64Encode(input.value, urlSafe?.checked);
        }

        output.value = result;
    } catch (error) {
        output.value = `Invalid Base64 input.\n${error.message}`;
    }
}

function base64Encode(value, urlSafe) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";

    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });

    let encoded = btoa(binary);

    if (urlSafe) {
        encoded = encoded.replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
    }

    return encoded;
}

function base64Decode(value, urlSafe) {
    let input = value.trim();

    if (urlSafe) {
        input = input.replaceAll("-", "+").replaceAll("_", "/");
        while (input.length % 4) input += "=";
    }

    const binary = atob(input);
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));

    return new TextDecoder().decode(bytes);
}

/* JWT */

async function runJwt(root) {
    const input = root.querySelector("[data-text-jwt-input]");
    const secret = root.querySelector("[data-text-jwt-secret]");
    const algorithm = root.querySelector("[data-text-jwt-algorithm]");
    const output = root.querySelector("[data-text-jwt-output]");
    const status = root.querySelector("[data-text-jwt-status]");

    if (!input || !output) return;

    const token = input.value.trim();

    if (!token) {
        output.value = "";
        if (status) status.textContent = "Paste a token to decode its header and payload.";
        return;
    }

    try {
        const parts = token.split(".");

        if (parts.length !== 3) {
            throw new Error("JWT must contain header, payload and signature parts.");
        }

        const header = JSON.parse(base64UrlDecode(parts[0]));
        const payload = JSON.parse(base64UrlDecode(parts[1]));

        let verification = "Not verified. Add a secret key to verify HS algorithms.";

        if (secret?.value.trim()) {
            const valid = await verifyJwtHmac(token, secret.value, algorithm?.value || "HS256");
            verification = valid ? "Signature valid." : "Signature invalid.";
        }

        output.value = JSON.stringify({
            header,
            payload,
            signature: parts[2],
            verification
        }, null, 2);

        if (status) {
            status.className = `text-tool-status ${verification.includes("valid.") ? "is-valid" : verification.includes("invalid") ? "is-invalid" : ""}`;
            status.textContent = verification;
        }
    } catch (error) {
        output.value = error.message;
        if (status) {
            status.className = "text-tool-status is-invalid";
            status.textContent = "Invalid JWT token.";
        }
    }
}

function base64UrlDecode(value) {
    let input = value.replaceAll("-", "+").replaceAll("_", "/");
    while (input.length % 4) input += "=";
    return base64Decode(input, false);
}

async function verifyJwtHmac(token, secret, algorithm) {
    const [header, payload, signature] = token.split(".");
    const data = `${header}.${payload}`;
    const expected = await hmac(data, secret, jwtAlgToHash(algorithm), "base64url");
    return timingSafeEqual(expected, signature);
}

function jwtAlgToHash(algorithm) {
    switch (algorithm) {
        case "HS384": return "SHA-384";
        case "HS512": return "SHA-512";
        case "HS256":
        default: return "SHA-256";
    }
}

/* Hash */

async function runHash(root) {
    const input = root.querySelector("[data-text-hash-input]");
    const algorithm = root.querySelector("[data-text-hash-algorithm]");
    const format = root.querySelector("[data-text-hash-format]");
    const expected = root.querySelector("[data-text-hash-expected]");
    const output = root.querySelector("[data-text-hash-output]");
    const status = root.querySelector("[data-text-hash-status]");

    if (!input || !algorithm || !format || !output) return;

    try {
        const result = algorithm.value === "MD5"
            ? md5(input.value)
            : await digest(input.value, algorithm.value, format.value);

        output.value = format.value === "hex-upper" ? result.toUpperCase() : result;

        const exp = expected?.value.trim();

        if (exp) {
            const valid = timingSafeEqual(normalizeCompare(output.value), normalizeCompare(exp));
            status.className = `text-tool-status ${valid ? "is-valid" : "is-invalid"}`;
            status.textContent = valid ? "Hash matches expected value." : "Hash does not match expected value.";
        } else {
            status.className = "text-tool-status";
            status.textContent = algorithm.value === "MD5" || algorithm.value === "SHA-1"
                ? "MD5 and SHA-1 are legacy algorithms and not recommended for security-sensitive use."
                : "Hashes are one-way and cannot be decrypted.";
        }
    } catch (error) {
        output.value = error.message;
    }
}

async function digest(value, algorithm, format) {
    const bytes = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest(algorithm, bytes);
    const array = Array.from(new Uint8Array(hash));

    if (format === "base64") return bytesToBase64(array);
    return array.map(byte => byte.toString(16).padStart(2, "0")).join("");
}

/* HMAC */

async function runHmac(root) {
    const message = root.querySelector("[data-text-hmac-message]");
    const secret = root.querySelector("[data-text-hmac-secret]");
    const algorithm = root.querySelector("[data-text-hmac-algorithm]");
    const expected = root.querySelector("[data-text-hmac-expected]");
    const output = root.querySelector("[data-text-hmac-output]");
    const status = root.querySelector("[data-text-hmac-status]");

    if (!message || !secret || !algorithm || !output) return;

    try {
        const result = await hmac(message.value, secret.value, algorithm.value, "hex");
        output.value = result;

        const exp = expected?.value.trim();

        if (exp) {
            const valid = timingSafeEqual(normalizeCompare(result), normalizeCompare(exp));
            status.className = `text-tool-status ${valid ? "is-valid" : "is-invalid"}`;
            status.textContent = valid ? "Signature is valid." : "Signature is invalid.";
        } else {
            status.className = "text-tool-status";
            status.textContent = "Enter an expected signature to verify it.";
        }
    } catch (error) {
        output.value = error.message;
    }
}

async function hmac(message, secret, algorithm, format) {
    const key = await crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(secret),
        { name: "HMAC", hash: algorithm },
        false,
        ["sign"]
    );

    const signature = await crypto.subtle.sign(
        "HMAC",
        key,
        new TextEncoder().encode(message)
    );

    const array = Array.from(new Uint8Array(signature));

    if (format === "base64url") {
        return bytesToBase64(array)
            .replaceAll("+", "-")
            .replaceAll("/", "_")
            .replaceAll("=", "");
    }

    if (format === "base64") return bytesToBase64(array);

    return array.map(byte => byte.toString(16).padStart(2, "0")).join("");
}

/* URL / HTML */

function runUrl(root) {
    const input = root.querySelector("[data-text-url-input]");
    const action = root.querySelector("[data-text-url-action]");
    const output = root.querySelector("[data-text-url-output]");

    if (!input || !action || !output) return;

    try {
        output.value = action.value === "decode"
            ? decodeURIComponent(input.value)
            : encodeURIComponent(input.value);
    } catch (error) {
        output.value = error.message;
    }
}

function runHtml(root) {
    const input = root.querySelector("[data-text-html-input]");
    const action = root.querySelector("[data-text-html-action]");
    const output = root.querySelector("[data-text-html-output]");

    if (!input || !action || !output) return;

    output.value = action.value === "decode"
        ? htmlDecode(input.value)
        : htmlEncode(input.value);
}

function htmlEncode(value) {
    return String(value)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

function htmlDecode(value) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
}

/* Query / Form */

function runQuery(root) {
    const input = root.querySelector("[data-text-query-input]");
    const action = root.querySelector("[data-text-query-action]");
    const output = root.querySelector("[data-text-query-output]");
    const table = root.querySelector("[data-text-query-table]");

    if (!input || !action || !output) return;

    if (action.value === "build") {
        const entries = parseDictionaryText(input.value);
        output.value = buildQueryString(entries);
        renderDictionary(table, entries);
        return;
    }

    const query = extractQuery(input.value);
    const entries = parseQueryString(query);

    output.value = JSON.stringify(entriesToObject(entries), null, 2);
    renderDictionary(table, entries);
}

function runForm(root) {
    const input = root.querySelector("[data-text-form-input]");
    const action = root.querySelector("[data-text-form-action]");
    const output = root.querySelector("[data-text-form-output]");
    const table = root.querySelector("[data-text-form-table]");

    if (!input || !action || !output) return;

    if (action.value === "build") {
        const entries = parseDictionaryText(input.value);
        output.value = buildFormPayload(entries);
        renderDictionary(table, entries);
        return;
    }

    const entries = parseQueryString(input.value);
    output.value = JSON.stringify(entriesToObject(entries), null, 2);
    renderDictionary(table, entries);
}

function extractQuery(value) {
    const text = value.trim();
    const index = text.indexOf("?");

    if (index >= 0) return text.slice(index + 1).split("#")[0];
    return text.replace(/^\?/, "");
}

function parseQueryString(value) {
    const params = new URLSearchParams(value);
    const entries = [];

    params.forEach((val, key) => {
        entries.push({ key, value: val });
    });

    return entries;
}

function parseDictionaryText(value) {
    const trimmed = value.trim();

    if (!trimmed) return [];

    try {
        const parsed = JSON.parse(trimmed);
        return Object.entries(parsed).flatMap(([key, val]) => {
            if (Array.isArray(val)) return val.map(x => ({ key, value: String(x) }));
            return [{ key, value: String(val) }];
        });
    } catch {
        return trimmed
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => {
                const separator = line.includes("=") ? "=" : ":";
                const index = line.indexOf(separator);

                if (index < 0) return { key: line, value: "" };

                return {
                    key: line.slice(0, index).trim(),
                    value: line.slice(index + 1).trim()
                };
            });
    }
}

function buildQueryString(entries) {
    const params = new URLSearchParams();

    entries.forEach(({ key, value }) => {
        if (key) params.append(key, value);
    });

    return `?${params.toString()}`;
}

function buildFormPayload(entries) {
    const params = new URLSearchParams();

    entries.forEach(({ key, value }) => {
        if (key) params.append(key, value);
    });

    return params.toString();
}

function entriesToObject(entries) {
    const obj = {};

    entries.forEach(({ key, value }) => {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            if (!Array.isArray(obj[key])) obj[key] = [obj[key]];
            obj[key].push(value);
        } else {
            obj[key] = value;
        }
    });

    return obj;
}

function renderDictionary(container, entries) {
    if (!container) return;

    if (!entries.length) {
        container.innerHTML = "";
        return;
    }

    container.innerHTML = `
        <div class="query-dictionary-table__head">
            <strong>Key</strong>
            <strong>Value</strong>
        </div>
        ${entries.map(item => `
            <div class="query-dictionary-table__row">
                <span>${escapeHtml(item.key)}</span>
                <span>${escapeHtml(item.value)}</span>
            </div>
        `).join("")}
    `;
}

/* Case */

function runCase(root) {
    const input = root.querySelector("[data-text-case-input]");
    const action = root.querySelector("[data-text-case-action]");
    const output = root.querySelector("[data-text-case-output]");

    if (!input || !action || !output) return;

    output.value = convertCase(input.value, action.value);
}

function convertCase(value, mode) {
    const words = String(value)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(/[\s_\-]+/)
        .filter(Boolean);

    switch (mode) {
        case "upper":
            return value.toUpperCase();
        case "lower":
            return value.toLowerCase();
        case "title":
            return words.map(capitalize).join(" ");
        case "sentence":
            return capitalize(value.toLowerCase());
        case "camel":
            return words.map((word, index) => index === 0 ? word.toLowerCase() : capitalize(word.toLowerCase())).join("");
        case "pascal":
            return words.map(word => capitalize(word.toLowerCase())).join("");
        case "snake":
            return words.map(word => word.toLowerCase()).join("_");
        case "kebab":
            return words.map(word => word.toLowerCase()).join("-");
        default:
            return value;
    }
}

function capitalize(value) {
    if (!value) return "";
    return value[0].toUpperCase() + value.slice(1);
}

/* Copy / helpers */

function bindCopyButtons(root) {
    root.querySelectorAll("[data-copy-target]").forEach(button => {
        button.addEventListener("click", async () => {
            const target = document.querySelector(button.dataset.copyTarget);
            const value = target?.value || target?.textContent || "";

            if (!value) return;

            try {
                await navigator.clipboard.writeText(value);
            } catch {
                fallbackCopy(value);
            }

            showDone(button);
        });
    });
}

function showDone(button) {
    const old = button.innerHTML;
    button.classList.add("is-done");
    button.innerHTML = `<i class="fa-regular fa-check"></i> Copied`;

    window.setTimeout(() => {
        button.innerHTML = old;
        button.classList.remove("is-done");
    }, 900);
}

function fallbackCopy(value) {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
}

function bytesToBase64(bytes) {
    let binary = "";
    bytes.forEach(byte => binary += String.fromCharCode(byte));
    return btoa(binary);
}

function normalizeCompare(value) {
    return String(value || "").trim().toLowerCase();
}

function timingSafeEqual(a, b) {
    if (a.length !== b.length) return false;

    let result = 0;

    for (let i = 0; i < a.length; i++) {
        result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }

    return result === 0;
}

function escapeHtml(value) {
    return String(value || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

/*
    Small browser-side MD5 implementation.
    MD5 is included for legacy checksum use only, not for secure password storage.
*/
function md5(input) {
    function rotateLeft(lValue, iShiftBits) {
        return (lValue << iShiftBits) | (lValue >>> (32 - iShiftBits));
    }

    function addUnsigned(lX, lY) {
        const lX4 = lX & 0x40000000;
        const lY4 = lY & 0x40000000;
        const lX8 = lX & 0x80000000;
        const lY8 = lY & 0x80000000;
        const lResult = (lX & 0x3FFFFFFF) + (lY & 0x3FFFFFFF);

        if (lX4 & lY4) return lResult ^ 0x80000000 ^ lX8 ^ lY8;
        if (lX4 | lY4) {
            if (lResult & 0x40000000) return lResult ^ 0xC0000000 ^ lX8 ^ lY8;
            return lResult ^ 0x40000000 ^ lX8 ^ lY8;
        }

        return lResult ^ lX8 ^ lY8;
    }

    function f(x, y, z) { return (x & y) | ((~x) & z); }
    function g(x, y, z) { return (x & z) | (y & (~z)); }
    function h(x, y, z) { return x ^ y ^ z; }
    function i(x, y, z) { return y ^ (x | (~z)); }

    function ff(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(f(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }

    function gg(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(g(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }

    function hh(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(h(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }

    function ii(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(i(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }

    function convertToWordArray(value) {
        const bytes = new TextEncoder().encode(value);
        const wordArray = [];
        const messageLength = bytes.length;
        let i;

        for (i = 0; i < messageLength - 3; i += 4) {
            wordArray[i >> 2] =
                bytes[i] |
                (bytes[i + 1] << 8) |
                (bytes[i + 2] << 16) |
                (bytes[i + 3] << 24);
        }

        let lastWord = 0;

        for (let j = 0; j < messageLength % 4; j++) {
            lastWord |= bytes[i + j] << (j * 8);
        }

        wordArray[i >> 2] = lastWord | (0x80 << ((messageLength % 4) * 8));
        wordArray[(((messageLength + 8) >> 6) + 1) * 16 - 2] = messageLength << 3;
        wordArray[(((messageLength + 8) >> 6) + 1) * 16 - 1] = messageLength >>> 29;

        return wordArray;
    }

    function wordToHex(value) {
        let result = "";

        for (let count = 0; count <= 3; count++) {
            const byte = (value >>> (count * 8)) & 255;
            result += (`0${byte.toString(16)}`).slice(-2);
        }

        return result;
    }

    const x = convertToWordArray(input);

    let a = 0x67452301;
    let b = 0xEFCDAB89;
    let c = 0x98BADCFE;
    let d = 0x10325476;

    for (let k = 0; k < x.length; k += 16) {
        const aa = a;
        const bb = b;
        const cc = c;
        const dd = d;

        a = ff(a, b, c, d, x[k + 0], 7, 0xD76AA478);
        d = ff(d, a, b, c, x[k + 1], 12, 0xE8C7B756);
        c = ff(c, d, a, b, x[k + 2], 17, 0x242070DB);
        b = ff(b, c, d, a, x[k + 3], 22, 0xC1BDCEEE);

        a = ff(a, b, c, d, x[k + 4], 7, 0xF57C0FAF);
        d = ff(d, a, b, c, x[k + 5], 12, 0x4787C62A);
        c = ff(c, d, a, b, x[k + 6], 17, 0xA8304613);
        b = ff(b, c, d, a, x[k + 7], 22, 0xFD469501);

        a = ff(a, b, c, d, x[k + 8], 7, 0x698098D8);
        d = ff(d, a, b, c, x[k + 9], 12, 0x8B44F7AF);
        c = ff(c, d, a, b, x[k + 10], 17, 0xFFFF5BB1);
        b = ff(b, c, d, a, x[k + 11], 22, 0x895CD7BE);

        a = ff(a, b, c, d, x[k + 12], 7, 0x6B901122);
        d = ff(d, a, b, c, x[k + 13], 12, 0xFD987193);
        c = ff(c, d, a, b, x[k + 14], 17, 0xA679438E);
        b = ff(b, c, d, a, x[k + 15], 22, 0x49B40821);

        a = gg(a, b, c, d, x[k + 1], 5, 0xF61E2562);
        d = gg(d, a, b, c, x[k + 6], 9, 0xC040B340);
        c = gg(c, d, a, b, x[k + 11], 14, 0x265E5A51);
        b = gg(b, c, d, a, x[k + 0], 20, 0xE9B6C7AA);

        a = gg(a, b, c, d, x[k + 5], 5, 0xD62F105D);
        d = gg(d, a, b, c, x[k + 10], 9, 0x02441453);
        c = gg(c, d, a, b, x[k + 15], 14, 0xD8A1E681);
        b = gg(b, c, d, a, x[k + 4], 20, 0xE7D3FBC8);

        a = gg(a, b, c, d, x[k + 9], 5, 0x21E1CDE6);
        d = gg(d, a, b, c, x[k + 14], 9, 0xC33707D6);
        c = gg(c, d, a, b, x[k + 3], 14, 0xF4D50D87);
        b = gg(b, c, d, a, x[k + 8], 20, 0x455A14ED);

        a = gg(a, b, c, d, x[k + 13], 5, 0xA9E3E905);
        d = gg(d, a, b, c, x[k + 2], 9, 0xFCEFA3F8);
        c = gg(c, d, a, b, x[k + 7], 14, 0x676F02D9);
        b = gg(b, c, d, a, x[k + 12], 20, 0x8D2A4C8A);

        a = hh(a, b, c, d, x[k + 5], 4, 0xFFFA3942);
        d = hh(d, a, b, c, x[k + 8], 11, 0x8771F681);
        c = hh(c, d, a, b, x[k + 11], 16, 0x6D9D6122);
        b = hh(b, c, d, a, x[k + 14], 23, 0xFDE5380C);

        a = hh(a, b, c, d, x[k + 1], 4, 0xA4BEEA44);
        d = hh(d, a, b, c, x[k + 4], 11, 0x4BDECFA9);
        c = hh(c, d, a, b, x[k + 7], 16, 0xF6BB4B60);
        b = hh(b, c, d, a, x[k + 10], 23, 0xBEBFBC70);

        a = hh(a, b, c, d, x[k + 13], 4, 0x289B7EC6);
        d = hh(d, a, b, c, x[k + 0], 11, 0xEAA127FA);
        c = hh(c, d, a, b, x[k + 3], 16, 0xD4EF3085);
        b = hh(b, c, d, a, x[k + 6], 23, 0x04881D05);

        a = hh(a, b, c, d, x[k + 9], 4, 0xD9D4D039);
        d = hh(d, a, b, c, x[k + 12], 11, 0xE6DB99E5);
        c = hh(c, d, a, b, x[k + 15], 16, 0x1FA27CF8);
        b = hh(b, c, d, a, x[k + 2], 23, 0xC4AC5665);

        a = ii(a, b, c, d, x[k + 0], 6, 0xF4292244);
        d = ii(d, a, b, c, x[k + 7], 10, 0x432AFF97);
        c = ii(c, d, a, b, x[k + 14], 15, 0xAB9423A7);
        b = ii(b, c, d, a, x[k + 5], 21, 0xFC93A039);

        a = ii(a, b, c, d, x[k + 12], 6, 0x655B59C3);
        d = ii(d, a, b, c, x[k + 3], 10, 0x8F0CCC92);
        c = ii(c, d, a, b, x[k + 10], 15, 0xFFEFF47D);
        b = ii(b, c, d, a, x[k + 1], 21, 0x85845DD1);

        a = ii(a, b, c, d, x[k + 8], 6, 0x6FA87E4F);
        d = ii(d, a, b, c, x[k + 15], 10, 0xFE2CE6E0);
        c = ii(c, d, a, b, x[k + 6], 15, 0xA3014314);
        b = ii(b, c, d, a, x[k + 13], 21, 0x4E0811A1);

        a = ii(a, b, c, d, x[k + 4], 6, 0xF7537E82);
        d = ii(d, a, b, c, x[k + 11], 10, 0xBD3AF235);
        c = ii(c, d, a, b, x[k + 2], 15, 0x2AD7D2BB);
        b = ii(b, c, d, a, x[k + 9], 21, 0xEB86D391);

        a = addUnsigned(a, aa);
        b = addUnsigned(b, bb);
        c = addUnsigned(c, cc);
        d = addUnsigned(d, dd);
    }

    return (wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d)).toLowerCase();
}